import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../services/axiosInstance';

const toMs = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const formatDateTime = (value) => {
  const ms = toMs(value);
  if (ms == null) return '-';
  return new Date(ms).toLocaleString();
};

const ordinal = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
};

const ProblemDrawer = ({ open, onClose, problemId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    problem: { problemId: null, title: null, difficulty: null },
    attempts: [],
    stats: { totalAttempts: 0, acceptedCount: 0, firstAcceptedAt: null },
  });
  const [newestFirst, setNewestFirst] = useState(true);

  useEffect(() => {
    if (!open || !problemId) return;

    const fetchProblem = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/student/progress/problem/${encodeURIComponent(problemId)}`);
        setData(
          response?.data || {
            problem: { problemId, title: null, difficulty: null },
            attempts: [],
            stats: { totalAttempts: 0, acceptedCount: 0, firstAcceptedAt: null },
          }
        );
      } catch (error) {
        console.log(error);
        setData({
          problem: { problemId, title: null, difficulty: null },
          attempts: [],
          stats: { totalAttempts: 0, acceptedCount: 0, firstAcceptedAt: null },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProblem();
  }, [open, problemId]);

  const orderedAttempts = useMemo(() => {
    const base = [...(data.attempts || [])]
      .map((attempt, idx) => ({ ...attempt, _idx: idx, _ms: toMs(attempt.submittedAt) }))
      .sort((a, b) => (a._ms ?? 0) - (b._ms ?? 0))
      .map((attempt, idx) => ({ ...attempt, order: idx + 1 }));

    if (newestFirst) {
      return [...base].sort((a, b) => (b._ms ?? 0) - (a._ms ?? 0));
    }
    return base;
  }, [data.attempts, newestFirst]);

  const firstAcceptedMs = toMs(data?.stats?.firstAcceptedAt);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
          backgroundImage: 'none',
          opacity: 1,
        },
      }}
    >
      <Box sx={{ p: 2.5, bgcolor: 'transparent', minHeight: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6">Problem Timeline</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">
            {data?.problem?.title || 'Problem'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Problem ID: {data?.problem?.problemId || problemId || '-'}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            {data?.problem?.difficulty ? (
              <Chip size="small" label={data.problem.difficulty} />
            ) : null}
            <Chip size="small" variant="outlined" label={`Attempts: ${data?.stats?.totalAttempts || 0}`} />
            <Chip size="small" variant="outlined" label={`Accepted: ${data?.stats?.acceptedCount || 0}`} />
          </Stack>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading attempts...' : 'Attempts'}
          </Typography>
          <Button size="small" variant="text" onClick={() => setNewestFirst((prev) => !prev)}>
            {newestFirst ? 'Newest first' : 'Oldest first'}
          </Button>
        </Box>

        <Stack spacing={1.2}>
          {!loading && orderedAttempts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No attempts found.
            </Typography>
          ) : null}

          {orderedAttempts.map((attempt) => {
            const isAccepted = String(attempt.status || '').toLowerCase() === 'accepted';
            const isFirstAccepted = isAccepted && firstAcceptedMs != null && attempt._ms === firstAcceptedMs;

            return (
              <Box
                key={`${attempt.order}-${attempt.submittedAt || 'na'}`}
                sx={{
                  border: '1px solid',
                  borderColor: isFirstAccepted ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  p: 1.25,
                  bgcolor: 'background.default',
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                  <Typography variant="body2" sx={{ fontWeight: isFirstAccepted ? 700 : 600 }}>
                    {ordinal(attempt.order)} attempt
                  </Typography>
                  <Stack direction="row" spacing={0.6}>
                    <Chip
                      size="small"
                      label={attempt.status || 'Unknown'}
                      color={isAccepted ? 'success' : 'default'}
                    />
                    {isFirstAccepted ? <Chip size="small" label="First Accepted" color="success" /> : null}
                  </Stack>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {formatDateTime(attempt.submittedAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`Language: ${attempt.language || '-'} | Runtime: ${attempt.runtime || '-'} | Memory: ${attempt.memory || '-'}`}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ProblemDrawer;
